import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShoppingCart, Check, Copy, X, Loader2, Coins } from 'lucide-react';
import { toast } from 'sonner';
import PaymentSuccessAnimation from '../components/PaymentSuccessAnimation';

export default function CreditShopPage() {
  const { api, user } = useAuthStore();
  const [adminPlans, setAdminPlans] = useState([]);
  const [masterPlans, setMasterPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      if (user?.role === 'admin') {
        // Admin vê os próprios pacotes oficiais
        const adminResponse = await api.get('/credit-plans');
        setAdminPlans(adminResponse.data);
        setMasterPlans([]);
      } else {
        // Todos os outros veem SÓ os do upline direto (o backend decide; se o upline é o admin, já inclui os oficiais)
        setAdminPlans([]);
        try {
          const masterResponse = await api.get('/master/credit-plans/available');
          setMasterPlans(masterResponse.data);
        } catch (e) {
          setMasterPlans([]);
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId, isMasterPlan = false, metodo = 'pix') => {
    if (purchasing) return;
    
    setPurchasing(planId);
    try {
      const endpoint = isMasterPlan 
        ? `/master/credit-plans/${planId}/purchase`
        : `/purchase-credits/${planId}`;
      const response = await api.post(endpoint, { metodo });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      setPaymentData(response.data);
      setPaymentDialog(true);
      
      startPaymentPolling(response.data.transaction_id || response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    } finally {
      setPurchasing(null);
    }
  };

  const startPaymentPolling = (transactionId) => {
    setCheckingPayment(true);
    const interval = setInterval(async () => {
      try {
        const response = await api.get('/transactions');
        const transaction = response.data.find(t => t.id === transactionId);
        
        if (transaction && transaction.status === 'approved') {
          clearInterval(interval);
          setCheckingPayment(false);
          setPaymentDialog(false);
          setShowSuccess(true);
        }
      } catch (error) {
        console.error('Erro ao verificar pagamento:', error);
      }
    }, 5000);

    setTimeout(() => {
      clearInterval(interval);
      setCheckingPayment(false);
    }, 600000);
  };

  const handleSuccessComplete = () => {
    setShowSuccess(false);
    window.location.reload();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const allPlans = [...adminPlans, ...masterPlans];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Coins className="w-8 h-8 text-primary" />
          Loja de Créditos
        </h1>
        <p className="text-muted-foreground">Compre créditos para renovar seus usuários</p>
        <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-500">
            💰 Seu saldo atual: <strong>{user?.credits || 0} créditos</strong>
          </p>
          <p className="text-xs text-blue-500/80 mt-1">
            Cada renovação consome 1 crédito. Mantenha sempre mais de 1 crédito para habilitar renovação automática.
          </p>
        </div>
        <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-xs text-yellow-500">
            ⚠️ Créditos são utilizados <strong>exclusivamente para renovação de painel dos seus revendedores</strong>. Não podem ser usados para renovar o seu próprio painel.
          </p>
        </div>
      </div>

      {/* Pacotes do Master (upline) */}
      {masterPlans.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">Pacotes do seu Master</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {masterPlans.map((plan) => (
              <Card key={`master-${plan.id}`} className="p-6 hover:border-primary transition-all border-2 border-primary/30">
                <div className="text-center space-y-4">
                  <div>
                    <h3 className="font-bold text-2xl mb-1">{plan.credits} Créditos</h3>
                    <p className="text-muted-foreground text-sm">{plan.name}</p>
                  </div>
                  
                  <div className="py-4 border-y border-border">
                    <p className="text-3xl font-bold text-primary">R$ {plan.price.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      R$ {(plan.price / plan.credits).toFixed(2)} por crédito
                    </p>
                  </div>
                  
                  {plan.checkout_unificado ? (
                    <Button onClick={() => handlePurchase(plan.id, true, 'cartao')} className="w-full" size="lg" disabled={purchasing === plan.id}>
                      {purchasing === plan.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando...</> : <><ShoppingCart className="w-4 h-4 mr-2" /> Comprar (PIX ou Cartão)</>}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button onClick={() => handlePurchase(plan.id, true, 'pix')} className="w-full" size="lg" disabled={purchasing === plan.id}>
                        {purchasing === plan.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando...</> : <><ShoppingCart className="w-4 h-4 mr-2" /> {plan.suporta_cartao ? 'Comprar com PIX' : 'Comprar'}</>}
                      </Button>
                      {plan.suporta_cartao && (
                        <Button onClick={() => handlePurchase(plan.id, true, 'cartao')} variant="outline" className="w-full border-primary/40" size="lg" disabled={purchasing === plan.id}>
                          💳 Comprar com cartão
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Pacotes do Admin (global) */}
      {adminPlans.length > 0 && (
        <div className="space-y-4">
          {masterPlans.length > 0 && (
            <h2 className="text-xl font-semibold text-foreground">Pacotes Oficiais</h2>
          )}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {adminPlans.map((plan) => (
              <Card key={`admin-${plan.id}`} className="p-6 hover:border-primary transition-all">
                <div className="text-center space-y-4">
                  <div>
                    <h3 className="font-bold text-2xl mb-1">{plan.credits} Créditos</h3>
                    <p className="text-muted-foreground text-sm">{plan.name}</p>
                  </div>
                  
                  <div className="py-4 border-y border-border">
                    <p className="text-3xl font-bold text-primary">R$ {plan.price.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      R$ {(plan.price / plan.credits).toFixed(2)} por crédito
                    </p>
                  </div>
                  
                  <Button 
                    onClick={() => handlePurchase(plan.id, false)}
                    className="w-full"
                    size="lg"
                    disabled={purchasing === plan.id}
                  >
                    {purchasing === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Comprar
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {allPlans.length === 0 && (
        <Card className="p-12 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Nenhum plano de credito disponivel no momento</p>
          <p className="text-sm text-muted-foreground/60 mt-2">Entre em contato com seu revendedor para comprar creditos.</p>
        </Card>
      )}

      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Pagamento via PIX</DialogTitle>
          </DialogHeader>
          
          {paymentData && (
            <div className="space-y-4">
              {checkingPayment && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-blue-500">Aguardando confirmação do pagamento...</p>
                </div>
              )}
              
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Escaneie o QR Code:</p>
                <img 
                  src={paymentData.qr_code ? (paymentData.qr_code.startsWith('http') ? paymentData.qr_code : paymentData.qr_code.startsWith('data:') ? paymentData.qr_code : `data:image/png;base64,${paymentData.qr_code}`) : (paymentData.qr_code_url || (paymentData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentData.qr_code_text)}` : ''))}
                  alt="QR Code PIX"
                  className="mx-auto w-64 h-64 border rounded-lg"
                />
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Ou copie o código PIX:</Label>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={paymentData.qr_code_text}
                    readOnly
                    className="flex-1 p-2 text-xs border rounded bg-muted font-mono"
                  />
                  <Button 
                    size="sm"
                    onClick={() => copyToClipboard(paymentData.qr_code_text)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-xs text-yellow-600">
                  ⏱️ Após o pagamento, a confirmação pode levar até 2 minutos.
                </p>
              </div>

              <Button 
                variant="outline" 
                onClick={() => setPaymentDialog(false)}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Animação de sucesso */}
      {showSuccess && (
        <PaymentSuccessAnimation 
          onComplete={handleSuccessComplete}
          message="Pagamento Confirmado!"
        />
      )}
    </div>
  );
}
